/**
 * UFC-only career stats computed from the public UFC Stats CSVs that
 * github.com/Greco1899/scrape_ufc_stats republishes daily (fight results,
 * per-round fight stats, fighter tale-of-the-tape, events). Pure logic: no
 * network. Only UFC fights are in this data, so a debut or Contender Series
 * fighter simply has no entry.
 */

export const UFC_STATS_SOURCE = 'https://github.com/Greco1899/scrape_ufc_stats';
export const UFC_STATS_FILES = {
  events: 'ufc_event_details.csv',
  results: 'ufc_fight_results.csv',
  stats: 'ufc_fight_stats.csv',
  tott: 'ufc_fighter_tott.csv',
} as const;

export interface UfcCareerStats {
  name: string;
  ufcStatsUrl: string | null;
  ufcFights: number;
  ufcRecord: { wins: number; losses: number; draws: number; noContests: number };
  /** Significant strikes landed / absorbed per minute. */
  slpm: number | null;
  sapm: number | null;
  /** Significant strike accuracy, and defense (share of opponents' attempts not landed). */
  strikeAccuracy: number | null;
  strikeDefense: number | null;
  /** Takedowns landed per 15 minutes, accuracy, and defense. */
  tdPer15: number | null;
  tdAccuracy: number | null;
  tdDefense: number | null;
  subAttemptsPer15: number | null;
  knockdownsPer15: number | null;
  /** Share of fight time spent in control. */
  controlShare: number | null;
  heightCm: number | null;
  reachCm: number | null;
  stance: string | null;
  birthDate: string | null;
  /** Most recent UFC fights first, at most 5. */
  recentUfcFights: {
    result: 'Win' | 'Loss' | 'Draw' | 'NC';
    opponent: string;
    method: string;
    date: string | null;
  }[];
}

/** Minimal RFC 4180 CSV parser (quoted fields, doubled quotes, CRLF). */
export function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  const [header, ...body] = rows.filter((r) => r.some((v) => v.trim() !== ''));
  if (!header) return [];
  const keys = header.map((h) => h.trim());
  return body.map((r) =>
    Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? '').trim()])),
  );
}

const normalize = (s: string): string =>
  s
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

/** "October 03, 2026" / "Oct 03, 2026" -> "2026-10-03". */
export function parseUfcDate(raw: string): string | null {
  const m = /^([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),\s*(\d{4})$/.exec(raw.trim());
  if (!m) return null;
  const month = MONTHS.indexOf(m[1].toLowerCase());
  if (month < 0) return null;
  return `${m[3]}-${String(month + 1).padStart(2, '0')}-${m[2].padStart(2, '0')}`;
}

/** "5' 11\"" -> 180; "--" -> null. */
export function feetInchesToCm(raw: string): number | null {
  const m = /(\d+)'\s*(\d+)?/.exec(raw);
  return m ? Math.round((+m[1] * 12 + (m[2] ? +m[2] : 0)) * 2.54) : null;
}

/** '74"' -> 188. */
export function inchesToCm(raw: string): number | null {
  const m = /^(\d+(?:\.\d+)?)"?$/.exec(raw.trim());
  return m ? Math.round(+m[1] * 2.54) : null;
}

/** "16 of 40" -> [16, 40]. */
export function landedOf(raw: string): [number, number] {
  const m = /(\d+)\s+of\s+(\d+)/.exec(raw);
  return m ? [+m[1], +m[2]] : [0, 0];
}

function clockSeconds(raw: string): number | null {
  const m = /^(\d+):(\d{2})$/.exec(raw.trim());
  return m ? +m[1] * 60 + +m[2] : null;
}

/**
 * Total fight time in seconds from the round the fight ended in, the time on
 * the clock, and the time format ("3 Rnd (5-5-5)", "1 Rnd + OT (12-3)").
 * Null when the format has no round lengths ("No Time Limit").
 */
export function fightSeconds(round: string, time: string, format: string): number | null {
  const r = Number(round);
  const t = clockSeconds(time);
  const lengths = /\(([\d-]+)\)/.exec(format)?.[1]?.split('-').map(Number);
  if (!(r >= 1) || t === null || !lengths || lengths.some((x) => !(x > 0))) return null;
  let seconds = t;
  for (let i = 0; i < r - 1; i++)
    seconds += (lengths[i] ?? lengths[lengths.length - 1]) * 60;
  return seconds;
}

interface Totals {
  seconds: number;
  sigLanded: number;
  sigAttempted: number;
  oppSigLanded: number;
  oppSigAttempted: number;
  tdLanded: number;
  tdAttempted: number;
  oppTdLanded: number;
  oppTdAttempted: number;
  subAttempts: number;
  knockdowns: number;
  controlSeconds: number;
}

const zero = (): Totals => ({
  seconds: 0,
  sigLanded: 0,
  sigAttempted: 0,
  oppSigLanded: 0,
  oppSigAttempted: 0,
  tdLanded: 0,
  tdAttempted: 0,
  oppTdLanded: 0,
  oppTdAttempted: 0,
  subAttempts: 0,
  knockdowns: 0,
  controlSeconds: 0,
});

const ratio = (a: number, b: number) => (b > 0 ? a / b : null);
const round3 = (x: number | null) => (x === null ? null : Number(x.toFixed(3)));

/**
 * Career stats for each wanted fighter (by name), from the four CSV texts.
 * A name that appears for more than one fighter in the tale-of-the-tape is
 * left out: there's no safe way to tell them apart by name alone.
 */
export function buildUfcCareerStats(
  csv: { events: string; results: string; stats: string; tott: string },
  wantedNames: readonly string[],
): Record<string, UfcCareerStats> {
  const wanted = new Map(wantedNames.map((n) => [normalize(n), n]));
  const eventDate = new Map(
    parseCsv(csv.events).map((e) => [e.EVENT.trim(), parseUfcDate(e.DATE)]),
  );

  const tottByName = new Map<string, Record<string, string>[]>();
  for (const t of parseCsv(csv.tott)) {
    const key = normalize(t.FIGHTER);
    if (!wanted.has(key)) continue;
    tottByName.set(key, [...(tottByName.get(key) ?? []), t]);
  }

  // Bout-level: seconds, outcome per fighter.
  const boutKey = (event: string, bout: string) => `${event.trim()}|${bout.trim()}`;
  const boutSeconds = new Map<string, number | null>();
  const out: Record<string, UfcCareerStats> = {};
  const totals = new Map<string, Totals>();
  const history = new Map<
    string,
    {
      result: 'Win' | 'Loss' | 'Draw' | 'NC';
      opponent: string;
      method: string;
      date: string | null;
    }[]
  >();
  for (const r of parseCsv(csv.results)) {
    const names = r.BOUT.split(/\s+vs\.\s+/);
    if (names.length !== 2) continue;
    const key = boutKey(r.EVENT, r.BOUT);
    boutSeconds.set(key, fightSeconds(r.ROUND, r.TIME, r['TIME FORMAT']));
    const outcomes = r.OUTCOME.split('/');
    names.forEach((name, i) => {
      const k = normalize(name);
      if (!wanted.has(k)) return;
      const o = (outcomes[i] ?? '').trim();
      const result = o === 'W' ? 'Win' : o === 'L' ? 'Loss' : o === 'D' ? 'Draw' : 'NC';
      history.set(k, [
        ...(history.get(k) ?? []),
        {
          result,
          opponent: names[1 - i].trim(),
          method: r.METHOD.trim(),
          date: eventDate.get(r.EVENT.trim()) ?? null,
        },
      ]);
    });
  }

  // Round-level stats: each row is one fighter in one round; pair rows by bout+round.
  const byBoutRound = new Map<string, Record<string, string>[]>();
  for (const s of parseCsv(csv.stats)) {
    if (!s.ROUND) continue;
    const key = `${boutKey(s.EVENT, s.BOUT)}|${s.ROUND}`;
    byBoutRound.set(key, [...(byBoutRound.get(key) ?? []), s]);
  }
  const countedBouts = new Set<string>();
  for (const [key, pair] of byBoutRound) {
    if (pair.length !== 2) continue;
    const bout = key.slice(0, key.lastIndexOf('|'));
    pair.forEach((me, i) => {
      const k = normalize(me.FIGHTER);
      if (!wanted.has(k)) return;
      const opp = pair[1 - i];
      const t = totals.get(k) ?? zero();
      const [sl, sa] = landedOf(me['SIG.STR.']);
      const [osl, osa] = landedOf(opp['SIG.STR.']);
      const [tl, ta] = landedOf(me.TD);
      const [otl, ota] = landedOf(opp.TD);
      t.sigLanded += sl;
      t.sigAttempted += sa;
      t.oppSigLanded += osl;
      t.oppSigAttempted += osa;
      t.tdLanded += tl;
      t.tdAttempted += ta;
      t.oppTdLanded += otl;
      t.oppTdAttempted += ota;
      t.subAttempts += Number(me['SUB.ATT']) || 0;
      t.knockdowns += Number(me.KD) || 0;
      t.controlSeconds += clockSeconds(me.CTRL) ?? 0;
      const fighterBout = `${k}|${bout}`;
      if (!countedBouts.has(fighterBout)) {
        countedBouts.add(fighterBout);
        t.seconds += boutSeconds.get(bout) ?? 0;
      }
      totals.set(k, t);
    });
  }

  for (const [k, name] of wanted) {
    const tott = tottByName.get(k);
    if (!tott || tott.length !== 1) continue;
    const t = tott[0];
    const h = (history.get(k) ?? []).sort((a, b) =>
      (b.date ?? '').localeCompare(a.date ?? ''),
    );
    const tot = totals.get(k) ?? zero();
    const minutes = tot.seconds / 60;
    out[k] = {
      name,
      ufcStatsUrl: t.URL || null,
      ufcFights: h.length,
      ufcRecord: {
        wins: h.filter((f) => f.result === 'Win').length,
        losses: h.filter((f) => f.result === 'Loss').length,
        draws: h.filter((f) => f.result === 'Draw').length,
        noContests: h.filter((f) => f.result === 'NC').length,
      },
      slpm: round3(ratio(tot.sigLanded, minutes)),
      sapm: round3(ratio(tot.oppSigLanded, minutes)),
      strikeAccuracy: round3(ratio(tot.sigLanded, tot.sigAttempted)),
      strikeDefense: round3(
        tot.oppSigAttempted > 0 ? 1 - tot.oppSigLanded / tot.oppSigAttempted : null,
      ),
      tdPer15: round3(minutes > 0 ? (tot.tdLanded / minutes) * 15 : null),
      tdAccuracy: round3(ratio(tot.tdLanded, tot.tdAttempted)),
      tdDefense: round3(
        tot.oppTdAttempted > 0 ? 1 - tot.oppTdLanded / tot.oppTdAttempted : null,
      ),
      subAttemptsPer15: round3(minutes > 0 ? (tot.subAttempts / minutes) * 15 : null),
      knockdownsPer15: round3(minutes > 0 ? (tot.knockdowns / minutes) * 15 : null),
      controlShare: round3(ratio(tot.controlSeconds, tot.seconds)),
      heightCm: feetInchesToCm(t.HEIGHT),
      reachCm: inchesToCm(t.REACH),
      stance: t.STANCE || null,
      birthDate: parseUfcDate(t.DOB),
      recentUfcFights: h.slice(0, 5),
    };
  }
  return out;
}
